import { getDataMarkets, getDataMarketsTags } from "@/api/dataset";
import type { DataMarketItem, GetDataMarketsParams } from "@/api/dataset";
import { Commit, Dispatch, Module } from "vuex";
import { RootState } from "@/store";
import { t } from "@/plugins/i18n";

export type Tag = {
  name: string;
  count: number;
  checked: boolean;
  code: number;
};

interface State {
  dataList: DataMarketItem[];
  isLoadingDataList: boolean;
  report: Tag[];
  file: Tag[];
  dataListParams: GetDataMarketsParams;
  selectedTags: Tag[];
  dataListPages: {
    pageNo: number;
    pageSize: number;
    orderBy: string;
    order: string;
    count: number;
    totalPage: number;
  };
  dataTagsParams: {
    name?: string;
  };
  selectSort: string;
  currentPageNo: number;
}
// 定义排序配置类型
type SortConfig = {
  orderBy: string;
  order: string;
  value: string;
};

// 定义排序映射类型
type SortMapType = {
  [key: string]: SortConfig;
};

// 排序选项映射表
const SORT_MAP: SortMapType = {
  relevance: { orderBy: "_score", order: "DESC", value: "0" },
  sales_desc: { orderBy: "t.sales_volume", order: "DESC", value: "1" },
  sales_asc: { orderBy: "t.sales_volume", order: "ASC", value: "2" },
  date_desc: { orderBy: "t.create_time", order: "DESC", value: "5" },
  date_asc: { orderBy: "t.create_time", order: "ASC", value: "6" },
  price_desc: { orderBy: "t.price", order: "DESC", value: "3" },
  price_asc: { orderBy: "t.price", order: "ASC", value: "4" },
};
const defaultFile = [
  {
    name: t("dataset.csv"),
    code: 3,
    count: 0,
    checked: false,
  },
  {
    name: t("dataset.text"),
    code: 1,
    count: 0,
    checked: false,
  },
  {
    name: t("dataset.api"),
    code: 3,
    count: 0,
    checked: false,
  },
  {
    name: t("dataset.video"),
    code: 2,
    count: 0,
    checked: false,
  },
  {
    name: t("dataset.other"),
    code: 6,
    count: 0,
    checked: false,
  },
  {
    name: t("dataset.image"),
    code: 4,
    count: 0,
    checked: false,
  },
  {
    name: t("dataset.zip"),
    code: 5,
    count: 0,
    checked: false,
  },
];
const store: Module<State, RootState> = {
  namespaced: true,
  state: () => ({
    dataList: [],
    dataListPages: {
      pageNo: 1,
      pageSize: 20,
      orderBy: "createTime",
      order: "desc",
      count: 0,
      totalPage: 0,
    },
    isLoadingDataList: false,
    report: [
      { name: t("dataset.haveReportCount"), count: 0, code: 1, checked: false },
      { name: t("dataset.noReportCount"), count: 0, code: 0, checked: false },
    ],
    file: [],
    dataListParams: {},
    dataTagsParams: {},
    selectedTags: [],
    selectSort: "0",
    currentPageNo: 1,
  }),

  mutations: {
    setDataList(state, payload) {
      state.dataList = payload.data;
      state.dataListPages = payload.page;
    },
    setIsLoadingDataList(state, payload) {
      state.isLoadingDataList = payload;
    },
    setParams(state, payload) {
      state.dataListParams = payload;
    },
    setTagsParams(state, payload) {
      state.dataTagsParams = payload;
    },
    setTags(state, payload) {
      state.selectedTags = payload;
    },
    setSort(state, payload) {
      state.selectSort = payload;
    },
    setCurrentPageNo(state, payload) {
      state.currentPageNo = payload;
    },
    setReport(state, payload = []) {
      state.report = payload;
    },
    setFile(state, payload) {
      state.file = payload;
    },
  },

  actions: {
    // 获取数据列表
    async getDataList({ commit, state, dispatch }) {
      try {
        commit("setIsLoadingDataList", true);
        const params = {
          ...state.dataListParams,
          pageNo: state.currentPageNo,
          pageSize: state.dataListPages.pageSize,
        };
        const res = await getDataMarkets(params);
        if (res === null) {
          commit("setDataList", {
            data: [],
            page: {
              pageNo: 1,
              pageSize: 20,
              orderBy: "createTime",
              order: "desc",
              count: 0,
              totalPage: 0,
            },
          });
          dispatch("popup/showPopup", 1, { root: true });
        } else {
          commit("setDataList", res);
        }
      } catch (error) {
        console.error(error);
      } finally {
        commit("setIsLoadingDataList", false);
      }
    },
    resetPageAndUpdateUrl({ commit }, router) {
      commit("setCurrentPageNo", 1);
      // 获取当前query并删除page参数
      const currentQuery = { ...router.currentRoute.value.query };
      delete currentQuery.page;
      // 更新URL，移除page参数
      router.replace({ query: currentQuery });
    },
    async getTags({ commit, state }: { commit: Commit; state: State }) {
      try {
        const res = await getDataMarketsTags(state.dataTagsParams);
        console.log("res:", res.patternList);
        const file: Tag[] = [];
        if (res.patternList && res.patternList.length > 0) {
          res.patternList.forEach((i) => {
            file.push({
              name: i.name,
              code: i.code,
              count: i.count,
              checked:
                state.file.find((item) => item.code === i.code)?.checked ||
                false,
            });
          });
        } else {
          file.push(...defaultFile);
        }
        console.log("file:", file);
        const report = state.report.map((i, idx) => {
          return {
            name: i.name,
            code: i.code,
            count: idx === 0 ? res.assessmentReport : res.assessmentReportNot,
            checked:
              state.report.find((item) => item.code === i.code)?.checked ||
              false,
          };
        });
        commit("setFile", file);
        commit("setReport", report);
      } catch (error) {
        log.error(error);
      }
    },
    getSelectReport(
      {
        commit,
        state,
        dispatch,
      }: { commit: Commit; state: State; dispatch: Dispatch },
      { payload, router }
    ) {
      let isAssessmentReport = state.dataListParams.isAssessmentReport;
      let newParams = { ...state.dataListParams };
      console.log("payload:", payload);
      if (payload.length) {
        if (payload.length > 1) {
          isAssessmentReport = 2;
        } else {
          isAssessmentReport = payload[0].code;
        }
        newParams = {
          ...newParams,
          isAssessmentReport,
        };
      } else {
        delete newParams.isAssessmentReport;
      }
      const newTags = [...state.report, ...state.file].filter(
        (item) => item.checked
      );
      const updatedReport = state.report.map((item) => {
        // 检查此report项是否在payload中被选中
        const selectedItem = payload.find(
          (p: { code: number }) => p.code === item.code
        );
        return {
          ...item,
          checked: selectedItem ? true : false,
        };
      });
      console.log(updatedReport);
      // 提交更新后的report状态
      commit("setReport", updatedReport);
      commit("setTags", newTags);
      commit("setParams", newParams);
      dispatch("resetPageAndUpdateUrl", router);
      dispatch("getDataList", newParams);
    },
    getSelectFile(
      {
        commit,
        state,
        dispatch,
      }: {
        commit: Commit;
        state: State;
        dispatch: Dispatch;
      },
      { payload, router }
    ) {
      let format = state.dataListParams.formats;
      const newParams = { ...state.dataListParams };
      if (payload.length) {
        format = payload.map((i: Tag) => i.code);
        newParams.formats = format;
      } else {
        delete newParams.formats;
      }
      const newTags = [...state.report, ...state.file].filter(
        (item) => item.checked
      );
      commit("setTags", newTags);
      commit("setParams", newParams);
      dispatch("resetPageAndUpdateUrl", router);
      dispatch("getDataList", newParams);
    },
    closeTag(
      {
        commit,
        state,
        dispatch,
      }: { commit: Commit; state: State; dispatch: Dispatch },
      { payload, router }
    ) {
      const newTags = state.selectedTags.filter(
        (item) => item.code !== payload.code
      );
      const reportTags = state.report.map((item) => {
        if (item.name === payload.name) {
          item.checked = false;
        }
        return item;
      });
      const fileTags = state.file.map((item) => {
        if (item.name === payload.name) {
          item.checked = false;
        }
        return item;
      });
      if (state.report.find((i) => i.name === payload.name)) {
        commit("setReport", reportTags);
        const selectedReport = reportTags.filter((i) => i.checked);
        dispatch("getSelectReport", { payload: selectedReport, router });
      }
      if (state.file.find((i) => i.name === payload.name)) {
        commit("setFile", fileTags);
        const selectedFile = fileTags.filter((i) => i.checked);
        dispatch("getSelectFile", { payload: selectedFile, router });
      }
      dispatch("resetPageAndUpdateUrl", router);
      commit("setTags", newTags);
    },
    // 处理排序
    goSort({ commit, state, dispatch }, { payload, router }) {
      console.log("router.currentRoute:", router.currentRoute);
      const currentQuery = { ...router.currentRoute.value.query };

      // 获取排序值
      let sortValue = "relevance";
      if (payload.orderBy === "t.sales_volume") {
        sortValue = "sales";
      } else if (payload.orderBy === "t.create_time") {
        sortValue = "date";
      } else if (payload.orderBy === "t.price") {
        sortValue = "price";
      }

      if (sortValue !== "relevance") {
        sortValue += `_${payload.order.toLowerCase()}`;
      }
      commit("setCurrentPageNo", 1);
      // 更新 URL，使用 replace
      const newQuery = { ...currentQuery, sort: sortValue };
      delete newQuery.page;
      router.replace({ query: newQuery });

      // 更新状态
      const newParams = {
        ...state.dataListParams,
        orderBy: payload.orderBy,
        order: payload.order,
      };
      commit("setParams", newParams);
      // dispatch("resetPageAndUpdateUrl", router);
      dispatch("getDataList");
    },

    // 处理分页
    goPage({ state: _state, dispatch, commit }, { payload, router }) {
      const currentQuery = { ...router.currentRoute.value.query };

      // 更新 URL，使用 replace
      const newQuery = {
        ...currentQuery,
        page: payload > 1 ? payload.toString() : undefined,
      };
      router.replace({ query: newQuery });

      commit("setCurrentPageNo", payload);
      dispatch("getDataList");
    },

    // 处理搜索
    goSearch({ commit, state, dispatch }, { payload, router, callback }) {
      const newParams = {
        ...state.dataListParams,
        name: payload.searchValue,
        recommendId: null,
        order: "",
        orderBy: "",
      };
      commit("setParams", newParams);
      commit("setSort", "0");
      commit("setTagsParams", { name: payload.searchValue });
      dispatch("resetTags", { payload: false, router });
      dispatch("resetReport", { payload: false, router });

      callback && callback();
    },

    // 从 URL 初始化状态
    initFromUrl({ commit, dispatch, state }, router) {
      const query = router.currentRoute.value.query;
      let _needsStateUpdate = false;

      // 构建新的参数对象
      const newParams = { ...state.dataListParams };

      // 处理搜索关键词
      if (query.search && query.search !== state.dataListParams.name) {
        newParams.name = query.search;
        newParams.recommendId = undefined;
        commit("setTagsParams", { name: query.search });
        _needsStateUpdate = true;
      }

      // 处理页码
      if (query.page) {
        const page = parseInt(query.page);
        if (!isNaN(page) && page > 0 && page !== state.currentPageNo) {
          commit("setCurrentPageNo", page);
          _needsStateUpdate = true;
        }
      }

      // 处理排序
      if (query.sort && SORT_MAP[query.sort]) {
        const sortConfig = SORT_MAP[query.sort];
        const currentOrderBy = state.dataListParams.orderBy;
        const currentOrder = state.dataListParams.order;

        if (
          sortConfig.orderBy !== currentOrderBy ||
          sortConfig.order !== currentOrder
        ) {
          newParams.orderBy = sortConfig.orderBy;
          newParams.order = sortConfig.order;
          commit("setSort", sortConfig.value);
          _needsStateUpdate = true;
        }
      }

      // 如果有任何状态更新，提交新参数并获取数据
      // if (needsStateUpdate) {
      commit("setParams", newParams);
      dispatch("getDataList");
      // 如果有搜索词变化，更新标签
      // if (query.search) {
      //   dispatch("getTags");
      // }
      // }
    },
    // 重置标签
    resetTags({ commit, state, dispatch }, { payload, router }) {
      commit("setTags", []);
      // commit(
      //   "setReport",
      //   state.report.map((i: Tag) => ({ ...i, checked: false }))
      // );
      commit(
        "setFile",
        state.file.map((i: Tag) => ({ ...i, checked: false }))
      );
      const newParams = { ...state.dataListParams };
      // delete newParams.isAssessmentReport;
      delete newParams.formats;
      commit("setParams", newParams);
      dispatch("resetPageAndUpdateUrl", router);

      payload && dispatch("getDataList");
    },
    resetReport(
      { commit, state: _state, dispatch: _dispatch },
      { payload: _payload, router: _router }
    ) {
      commit(
        "setReport",
        _state.report.map((i: Tag) => ({ ...i, checked: false }))
      );
      const newParams = { ..._state.dataListParams };
      delete newParams.isAssessmentReport;
      commit("setParams", newParams);
    },
  },
};

export default store;
